import { alpha, darken, lighten } from "@material-ui/core/styles";
import { UI_DENSITY } from "./density";

export const DEFAULT_APP_NAME = "Dominium Chat";
export const DEFAULT_PRIMARY_LIGHT = "#315bce";
export const DEFAULT_PRIMARY_DARK = "#91adff";

export function resolvePrimaryColor(value, mode = "light") {
  const fallback = mode === "dark" ? DEFAULT_PRIMARY_DARK : DEFAULT_PRIMARY_LIGHT;
  // Old template defaults adopt the new identity; custom company colors remain supported.
  if (!/^#[\da-f]{6}$/i.test(value || "") || /^(#065183|#0000ff|#39ace7)$/i.test(value)) return fallback;
  return value;
}

export function corporateTheme(mode, primary) {
  const dark = mode === "dark";
  const surface = dark ? "#182231" : "#ffffff";
  const canvas = dark ? "#101823" : "#f3f5f9";
  const border = dark ? "#2c394b" : "#e5eaf1";
  const text = dark ? "#edf2fa" : "#18263c";
  const muted = dark ? "#a4b2c7" : "#64748b";
  return {
    breakpoints: { values: { xs: 0, sm: 600 * UI_DENSITY, md: 960 * UI_DENSITY, lg: 1280 * UI_DENSITY, xl: 1920 * UI_DENSITY } },
    palette: {
      type: mode,
      mode,
      primary: { main: primary, light: lighten(primary, 0.15), dark: darken(primary, 0.18), contrastText: dark ? "#101823" : "#ffffff" },
      secondary: { main: dark ? "#6ed9c5" : "#167d73" },
      background: { default: canvas, paper: surface },
      text: { primary: text, secondary: muted },
      divider: border,
      textPrimary: text, borderPrimary: border, fontColor: text,
      dark: { main: text }, light: { main: surface },
      tabHeaderBackground: canvas, optionsBackground: surface, fancyBackground: canvas,
      total: surface, messageIcons: muted, inputBackground: surface, barraSuperior: surface,
    },
    typography: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontSize: 14,
      h1: { fontWeight: 700, letterSpacing: "-0.04em" },
      h2: { fontWeight: 700, letterSpacing: "-0.035em" },
      h3: { fontWeight: 700, letterSpacing: "-0.03em" },
      h4: { fontWeight: 700, letterSpacing: "-0.03em" },
      h5: { fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.025em" },
      h6: { fontWeight: 600, letterSpacing: "-0.02em" },
      button: { textTransform: "none", fontWeight: 600, letterSpacing: 0 },
    },
    shape: { borderRadius: 10 },
    props: { MuiButton: { disableElevation: true } },
    overrides: {
      MuiCssBaseline: { "@global": {
        html: { fontSize: `${UI_DENSITY * 100}%` },
        body: { backgroundColor: canvas, color: text },
        "::selection": { background: alpha(primary, 0.18) },
        "*:focus-visible": { outline: `2px solid ${primary}`, outlineOffset: 3 },
      } },
      MuiButton: { root: { borderRadius: 8, padding: "9px 16px", transition: "background-color 160ms ease", textTransform: "none" }, contained: { boxShadow: "none" }, outlined: { borderColor: border } },
      MuiPaper: { root: { backgroundImage: "none" }, rounded: { borderRadius: 12 }, elevation1: { boxShadow: "0 2px 8px rgba(20, 35, 60, 0.04)" } },
      MuiDialog: { paper: { border: `1px solid ${border}` } },
      MuiDialogTitle: { root: { padding: "24px 24px 16px" } },
      MuiDialogActions: { root: { padding: "16px 24px", borderTop: `1px solid ${border}` } },
      MuiOutlinedInput: { root: { borderRadius: 8 }, notchedOutline: { borderColor: border } },
      MuiTab: { root: { textTransform: "none", fontWeight: 600 } },
      MuiTabs: { indicator: { height: 3, borderRadius: 3 } },
      MuiTableCell: { root: { borderBottom: `1px solid ${border}`, padding: "14px 16px" }, head: { backgroundColor: canvas, color: muted, fontSize: 12, fontWeight: 600 } },
      MuiChip: { root: { borderRadius: 6, fontWeight: 500 } },
      MuiTooltip: { tooltip: { backgroundColor: "#18263c", borderRadius: 6, fontSize: 12, padding: "8px 10px" } },
      MuiAppBar: { root: { boxShadow: "none" } },
    },
    scrollbarStyles: {
      "&::-webkit-scrollbar": { width: 6, height: 6 },
      "&::-webkit-scrollbar-thumb": { backgroundColor: dark ? "#455269" : "#c4cedb", borderRadius: 6 },
      "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
    },
    scrollbarStylesSoft: {
      "&::-webkit-scrollbar": { width: 6, height: 6 },
      "&::-webkit-scrollbar-thumb": { backgroundColor: dark ? "#455269" : "#c4cedb", borderRadius: 6 },
    },
  };
}
